import React, { useMemo } from 'react'
import { CreditDocument } from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerActions, DrawerView } from '@divvy-web/skylab.drawer'
import Dropdown from '@divvy-web/skylab.dropdown'
import Form2, { FormField2 } from '@divvy-web/skylab.form2'
import ListItem from '@divvy-web/skylab.listitem'
import { useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import Scrim from '@divvy-web/skylab.scrim'

import { useDocumentTypes } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/DocumentsTabContent/useDocumentTypes'
import { useGetCreditApplicationDetails } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import { type DocumentTypeValidationSchema, documentTypeValidationSchema } from './documentTypeValdiationSchema'
import styles from './styles.module.scss'

export interface SelectDocumentTypeDrawerProps {
  salesforceCreditId: string
  isShowing: boolean
  onCancel: () => void
  onDocumentTypeConfirmation: (documentType: CreditDocument) => Promise<void>
}

const SelectDocumentTypeDrawer = ({
  salesforceCreditId,
  isShowing,
  onCancel,
  onDocumentTypeConfirmation,
}: SelectDocumentTypeDrawerProps) => {
  const [getClassName] = useNameFormatter('SelectDocumentTypeDrawer')
  const { track } = useWebTracking()

  const { data } = useGetCreditApplicationDetails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
  })

  const requiredDocuments = useMemo(() => data?.creditApplication?.requiredDocs ?? [], [data])

  const form = useForm<DocumentTypeValidationSchema>({
    resolver: yupResolver(documentTypeValidationSchema),
  })

  const { control, handleSubmit, reset } = form

  const { isValid, isSubmitting } = useFormState({ control })

  const { options } = useDocumentTypes({ salesforceCreditId })

  const requiredItems = useMemo(
    () => options.filter(({ value }) => requiredDocuments.some((doc) => doc.documentType === value)),
    [options, requiredDocuments],
  )

  const otherItems = useMemo(
    () => options.filter(({ value }) => !requiredDocuments.some((doc) => doc.documentType === value)),
    [options, requiredDocuments],
  )

  const onCancelForm = () => {
    track('se_signup__application_dashboard_documents_tab_modal__cancelled')
    reset()
    onCancel()
  }

  const onSubmit = handleSubmit(async ({ documentType: { value } }) => {
    await onDocumentTypeConfirmation(value as CreditDocument)
    reset()
  })

  return (
    <Scrim isShowing={isShowing}>
      <Drawer
        activeViewKey='default'
        className={`${styles[getClassName('')]} dd-privacy-allow`}
        dataTestId='select-document-type'
        isShowing={isShowing}
        views={{
          default: (
            <DrawerView
              className='dd-privacy-allow'
              footerActions={
                <DrawerActions
                  cancelText={
                    <span className='dd-privacy-allow'>
                      <FormattedMessage
                        defaultMessage='Cancel'
                        id='sputnik.DocumentUploadTypeModal__47FYwb'
                      />
                    </span>
                  }
                  className={styles[getClassName('actions')]}
                  confirmButtonColor='action'
                  confirmText={
                    <span className='dd-privacy-allow'>
                      <FormattedMessage
                        defaultMessage='Submit'
                        id='sputnik.DocumentUploadTypeModal__wSZR47'
                      />
                    </span>
                  }
                  inactive={!isValid || isSubmitting}
                  onCancel={onCancelForm}
                  onConfirm={onSubmit}
                />
              }
              title={
                <FormattedMessage
                  defaultMessage='Select document'
                  id='sputnik.DocumentUploadTypeModal__t+2Jv7'
                />
              }
              onCloseClick={onCancelForm}
            >
              <div className={`${styles[getClassName('content')]} dd-privacy-allow`}>
                <Form2 {...form}>
                  <FormField2
                    control={control}
                    label={
                      <FormattedMessage
                        defaultMessage='Document'
                        id='sputnik.DocumentUploadTypeModal__wmirkP'
                      />
                    }
                    name='documentType'
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        isPortal
                        isSearchable
                        onSelect={(selectedItem) => field.onChange(selectedItem)}
                      >
                        {({ getItemProps, highlightedIndex }) => {
                          if (requiredItems.length > 0) {
                            return (
                              <div>
                                <div
                                  aria-level={1}
                                  className={styles[getClassName('category-title')]}
                                  role='heading'
                                >
                                  <FormattedMessage
                                    defaultMessage='Required'
                                    id='sputnik.SelectDocumentTypeDrawer__Seanpx'
                                  />
                                </div>
                                {requiredItems.map((item, index) => (
                                  <ListItem
                                    isHighlighted={highlightedIndex === index}
                                    isSelected={field.value === item.value}
                                    label={item.label}
                                    value={item.value}
                                    {...getItemProps({
                                      index,
                                      item,
                                      key: item.value,
                                    })}
                                    key={item.value}
                                  />
                                ))}
                                {otherItems.length > 0 && (
                                  <>
                                    <div
                                      aria-level={1}
                                      className={styles[getClassName('category-title')]}
                                      role='heading'
                                    >
                                      <FormattedMessage
                                        defaultMessage='Other'
                                        id='sputnik.SelectDocumentTypeDrawer__/VnDMl'
                                      />
                                    </div>
                                    {otherItems.map((item, index) => (
                                      <ListItem
                                        isHighlighted={highlightedIndex === index + requiredItems.length}
                                        isSelected={field.value === item.value}
                                        label={item.label}
                                        value={item.value}
                                        {...getItemProps({
                                          index: index + requiredItems.length,
                                          item,
                                          key: item.value,
                                        })}
                                        key={item.value}
                                      />
                                    ))}
                                  </>
                                )}
                              </div>
                            )
                          }

                          return options.map((item, index) => (
                            <ListItem
                              isHighlighted={highlightedIndex === index}
                              isSelected={field.value === item.value}
                              label={item.label}
                              value={item.value}
                              {...getItemProps({
                                index,
                                item,
                                key: item.value,
                              })}
                              key={item.value}
                            />
                          ))
                        }}
                      </Dropdown>
                    )}
                  />
                </Form2>
              </div>
            </DrawerView>
          ),
        }}
      />
    </Scrim>
  )
}

export default SelectDocumentTypeDrawer
